import React, { useState } from 'react';
import { CalendarDays, Clock3, Eye } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import { cn } from '../../../lib/utils';
import { LegacyCptReport } from '../../../services/legacyCptReportService';
import LegacyCptReportPreview from './LegacyCptReportPreview';
import {
    getLegacyCptCodeLabels,
    getLegacyReportSummary,
    getLegacyServiceDateLabel,
    getLegacyStatusMeta,
    getLegacyUpdatedAtLabel,
} from './legacyCptReportUtils';

interface LegacyCptReportCardProps {
    report: LegacyCptReport;
}

const CPT_BADGE_TEXT_CLASS = 'text-[#6AA2FF] border-[#6AA2FF]';

const LegacyCptReportCard: React.FC<LegacyCptReportCardProps> = ({
    report,
}) => {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const statusMeta = getLegacyStatusMeta(report);
    const updatedAtLabel = getLegacyUpdatedAtLabel(report);
    const serviceDateLabel = getLegacyServiceDateLabel(report);
    const summary = getLegacyReportSummary(report);
    const codeLabels = getLegacyCptCodeLabels(report);

    return (
        <Card>
            <CardContent className="p-5 md:p-6">
                <div className="space-y-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1 space-y-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge variant="outline">
                                    {report.reportId || 'Legacy report'}
                                </Badge>
                                <Badge
                                    variant="outline"
                                    className="border-amber-500/40 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-200"
                                >
                                    {statusMeta.label +
                                        (statusMeta.label === 'In Progress'
                                            ? '(Locked)'
                                            : '')}
                                </Badge>
                                <div
                                    className={cn(
                                        'flex items-center gap-2 text-xs',
                                        'text-muted-foreground'
                                    )}
                                >
                                    <Clock3 className="h-3.5 w-3.5" />
                                    <span>Latest updated</span>
                                    <span className="font-medium text-foreground">
                                        {updatedAtLabel}
                                    </span>
                                </div>
                                <Dialog
                                    open={isPreviewOpen}
                                    onOpenChange={setIsPreviewOpen}
                                >
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setIsPreviewOpen(true)}
                                    >
                                        <Eye className="h-4 w-4" />
                                        Preview Report
                                    </Button>
                                    <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
                                        <DialogHeader className="shrink-0 border-b px-6 pb-4 pt-6 pr-14">
                                            <DialogTitle>
                                                Legacy CPT Report Preview
                                            </DialogTitle>
                                        </DialogHeader>
                                        <LegacyCptReportPreview
                                            report={report}
                                            className="min-h-0 flex-1"
                                            onClose={() =>
                                                setIsPreviewOpen(false)
                                            }
                                        />
                                    </DialogContent>
                                </Dialog>
                            </div>
                            <p className="max-w-3xl text-sm leading-7 md:text-base">
                                {summary}
                            </p>
                            <p className="text-xs leading-5 text-muted-foreground">
                                Legacy CPT reports are view-only and remain
                                outside the modular case workflow.
                            </p>
                        </div>
                        {codeLabels.length > 0 && (
                            <div className="flex flex-col gap-2">
                                {codeLabels.map((code) => (
                                    <Badge
                                        key={code}
                                        variant="outline"
                                        className={CPT_BADGE_TEXT_CLASS}
                                    >
                                        {code}
                                    </Badge>
                                ))}
                            </div>
                        )}
                        <div className="space-y-3 lg:min-w-[220px]">
                            <Card className="border-border/50 bg-[#fafafa] dark:border-white/10 dark:bg-[#1a1a1a]">
                                <CardContent className="space-y-0.5 p-3">
                                    <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-black/70 dark:text-white/70">
                                        <CalendarDays className="h-3 w-3" />
                                        Date of service
                                    </div>
                                    <div className="text-sm font-medium leading-tight text-black dark:text-white">
                                        {serviceDateLabel}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default LegacyCptReportCard;
